/**
 * POS Products Table Component
 * @see sprint-datagrid-migration Phase 2.2
 */

import { useMemo, useCallback } from 'react';
import { ShoppingBag, RefreshCw, ChefHat } from 'lucide-react';
import {
  DataGrid,
  Badge,
  EmptyState,
  textColumn,
  moneyColumn,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import { useCurrency } from '@/hooks';
import { CostBadge } from './CostBadge';
import type { PosProductListItem } from '../services/pos-products.service';

export type SortField = 'name' | 'sku' | 'type' | 'posCategory' | 'sellPrice' | 'syncStatus';
export type SortOrder = 'asc' | 'desc';

const SORT_FIELD_MAP: Record<string, SortField> = {
  name: 'name',
  sku: 'sku',
  type: 'type',
  posCategory: 'posCategory',
  sellPrice: 'sellPrice',
};

interface PosProductsTableProps {
  products: PosProductListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  sortBy: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField, order: SortOrder) => void;
  onRowClick?: (productId: string) => void;
  onAssociateRecipe?: (productId: string) => void;
  isLoading?: boolean;
  hasFilters?: boolean;
  onSyncClick?: () => void;
}

function getTypeBadge(type: PosProductListItem['type']) {
  switch (type) {
    case 'PRODUCT':
      return <Badge variant="primary">Produit</Badge>;
    case 'OPTION':
      return <Badge variant="neutral">Option</Badge>;
    default:
      return <Badge variant="neutral">{type}</Badge>;
  }
}

function RecipeBadge({
  hasRecipe,
  onAssociate,
}: {
  hasRecipe: boolean | undefined;
  onAssociate?: () => void;
}) {
  if (hasRecipe) {
    return (
      <span className="text-success-600" title="Recette associée">
        <ChefHat className="w-4 h-4" />
      </span>
    );
  }
  if (onAssociate) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onAssociate();
        }}
        className="text-danger-400 hover:text-danger-600 transition-colors"
        title="Associer une recette"
      >
        <ChefHat className="w-4 h-4" />
      </button>
    );
  }
  return (
    <span className="text-danger-400" title="Sans recette">
      <ChefHat className="w-4 h-4" />
    </span>
  );
}

export function PosProductsTable({
  products,
  pagination,
  onPageChange,
  onLimitChange,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  onAssociateRecipe,
  isLoading,
  hasFilters,
  onSyncClick,
}: PosProductsTableProps) {
  const currency = useCurrency();

  const columns = useMemo<DataGridColumn<PosProductListItem>[]>(
    () => [
      textColumn<PosProductListItem>({
        id: 'name',
        header: 'Nom POS',
        accessor: 'name',
        ellipsis: true,
        maxWidth: '280px',
        cell: (r) => (
          <div className="flex items-center gap-2">
            <RecipeBadge
              hasRecipe={r.hasRecipe}
              onAssociate={
                !r.hasRecipe && onAssociateRecipe
                  ? () => onAssociateRecipe(r.id)
                  : undefined
              }
            />
            <span className="font-medium truncate">{r.name}</span>
          </div>
        ),
      }),
      textColumn<PosProductListItem>({
        id: 'sku',
        header: 'SKU',
        accessor: 'sku',
        cellClassName: 'font-mono text-xs text-neutral-500',
      }),
      textColumn<PosProductListItem>({
        id: 'type',
        header: 'Type',
        accessor: 'type',
        cell: (r) => getTypeBadge(r.type),
      }),
      textColumn<PosProductListItem>({
        id: 'posCategory',
        header: 'Catégorie',
        accessor: 'posCategory',
        cell: (r) => (
          <span className="text-neutral-500">{r.posCategory || '—'}</span>
        ),
      }),
      moneyColumn<PosProductListItem>({
        id: 'sellPrice',
        header: 'Prix vente',
        accessor: (r) => (r.sellPrice != null ? r.sellPrice / 100 : null),
      }),
      textColumn<PosProductListItem>({
        id: 'theoreticalCost',
        header: 'Coût',
        sortable: false,
        align: 'right',
        exportValue: (r) =>
          r.theoreticalCost !== null ? r.theoreticalCost / 100 : null,
        cell: (r) => (
          <CostBadge
            cost={r.theoreticalCost}
            hasRecipe={r.theoreticalCost !== null}
            currency={currency}
            size="sm"
          />
        ),
      }),
    ],
    [currency, onAssociateRecipe]
  );

  // ---- Sort (server-side, controlled) ------------------------------------
  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(
      ([, v]) => v === sortBy
    )?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!next) {
        onSort('name', 'asc');
        return;
      }
      const backendField = SORT_FIELD_MAP[next.columnId];
      if (backendField) onSort(backendField, next.direction);
    },
    [onSort]
  );

  if (!isLoading && products.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={ShoppingBag}
        title="Aucun produit trouvé"
        description="Aucun produit ne correspond à vos critères. Essayez de modifier vos filtres."
      />
    ) : (
      <EmptyState
        icon={ShoppingBag}
        title="Aucun produit POS importé"
        description="Lancez une synchronisation pour importer vos produits depuis votre logiciel de caisse."
        action={
          onSyncClick
            ? { label: 'Synchroniser maintenant', onClick: onSyncClick, icon: RefreshCw }
            : undefined
        }
      />
    );
  }

  return (
    <DataGrid<PosProductListItem>
      data={products}
      columns={columns}
      rowKey={(r) => r.id}
      isLoading={isLoading}
      skeletonRows={10}
      onRowClick={onRowClick ? (r) => onRowClick(r.id) : undefined}
      currency={currency}
      sortState={sortState}
      onSortChange={handleSortChange}
      exportable={{
        fileName: { base: 'produits_pos' },
        sheetName: 'Produits POS',
        position: 'header',
      }}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: pagination.totalPages,
        onPageChange,
        onLimitChange,
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucun produit POS trouvé
        </div>
      }
    />
  );
}
